import * as React from 'react';
import { Box } from '@apia/theme';
import {
  getFocusSelector,
  isChild,
  notUnfocusableSelector,
} from '../../../utils/dom';
import TabsController, { TTab, TToggleEvent } from './TabsController';
import useTabs from './useTabs';
import TabRender from './TabRender';
import focus from '../../../classes/focusController';

export const TabsContext = React.createContext<
  | {
      close: (focus?: boolean) => void;
      open: (focus?: boolean) => void;
      tabId: TTab['id'];
    }
  | undefined
>(undefined);
TabsContext.displayName = 'TabsContext';

export function useTabContext() {
  const context = React.useContext(TabsContext);
  if (!context) throw new Error('There is no tab context');
  return context;
}

const Render = ({ children }: { children?: React.ReactNode }) => {
  const tabs = useTabs();
  const [ref, setRef] = React.useState<HTMLDivElement | undefined>(undefined);

  React.useEffect(() => {
    const handleShortcuts = (ev: KeyboardEvent) => {
      if (ev.code === undefined) return;
      const match = ev.code.match(/^Digit([0-9])$/);
      if (match && ev.ctrlKey && ev.shiftKey) {
        const key = Number.parseInt(match[1], 10) - 1;

        if (key < tabs.length) {
          const { history } = TabsController;

          const isOpen =
            history[history.length - 1]?.id !== tabs[key].id ||
            !tabs[key].open ||
            !!(
              document.activeElement &&
              !isChild(
                document.activeElement as HTMLElement,
                (parent) => parent.id === history[history.length - 1].id,
              )
            );

          TabsController.toggle(tabs[key].id, isOpen);
        }
      }
    };

    document.addEventListener('keyup', handleShortcuts);

    function handleToggle({ open, id, focus: doFocus }: TToggleEvent) {
      if (open && doFocus) {
        const tab = ref?.querySelector(`#${id}`) as HTMLDivElement;
        if (tab) {
          let focusElement: HTMLElement = tab.querySelector(
            getFocusSelector(
              `${notUnfocusableSelector}:not([aria-hidden="true"] *)`,
            ),
          ) as HTMLElement;

          const toggleAccordionElementButton = tab.querySelector(
            'button.toggleAccordionElement',
          ) as HTMLButtonElement;

          if (!focusElement) {
            if (toggleAccordionElementButton)
              focusElement = toggleAccordionElementButton;
            else focusElement = tab;
          }

          console.log({ toggleAccordionElementButton, focusElement });

          if (focusElement) {
            void focus.on(focusElement, {
              scrollIntoViewOptions: { behavior: 'auto', block: 'center' },
              dispatchCustomEvent: false,
            });
          }

          toggleAccordionElementButton?.scrollIntoView(true);
        }
      } else if (!open) {
        const lastOpenTab = TabsController.history
          .reverse()
          .find((tab) => tab.open);
        if (lastOpenTab) {
          TabsController.toggle(lastOpenTab.id, true);
        } else {
          const firstTabOpen = TabsController.tabs.find((tab) => tab.open);
          if (firstTabOpen) TabsController.toggle(firstTabOpen?.id, true);
        }
      }
    }

    TabsController.on('toggle', handleToggle);
    TabsController.on('open', handleToggle);

    return () => {
      document.removeEventListener('keyup', handleShortcuts);
      TabsController.off('toggle', handleToggle);
      TabsController.off('open', handleToggle);
    };
  }, [tabs, ref]);

  return (
    <Box
      className="tabs__render"
      {...window.getVariant('layout.common.tabs.render')}
      ref={setRef}
    >
      {tabs.map((tab) => {
        return (
          <TabRender key={tab.id} id={tab.id}>
            {tab.children}
          </TabRender>
        );
      })}
      {children}
    </Box>
  );
};

export default React.memo(Render);
